import React, { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';

import { TURNSTILE_SITE_KEY } from 'constants/envVars';
import { loadTurnstile } from 'helpers/turnstileLoader';

/**
 * Renders a Cloudflare Turnstile widget and hands the resulting token upwards.
 *
 * Renders nothing at all when no site key is configured, which is what keeps
 * local development working without a Cloudflare account. The backend applies
 * the matching rule only when its own secret is set, so the two switch on
 * together.
 *
 * Tokens are single use. Bump `resetSignal` after every rejected submit to swap
 * the spent token for a fresh one.
 */
function TurnstileWidget({ onVerify, onExpire, resetSignal = 0, className = '' }) {
  const { i18n } = useTranslation();
  const containerRef = useRef(null);
  const widgetIdRef = useRef(null);

  // Held in refs so a changed callback identity never re-renders the widget.
  const onVerifyRef = useRef(onVerify);
  const onExpireRef = useRef(onExpire);
  onVerifyRef.current = onVerify;
  onExpireRef.current = onExpire;

  // The app labels its only locale "ge", Cloudflare expects the ISO code "de".
  const appLanguage = i18n?.language ?? '';
  const language = appLanguage.startsWith('ge') || appLanguage.startsWith('de') ? 'de' : 'auto';

  useEffect(() => {
    if (!TURNSTILE_SITE_KEY) {
      return undefined;
    }

    let cancelled = false;

    loadTurnstile()
      .then((turnstile) => {
        if (cancelled || !containerRef.current) {
          return;
        }

        widgetIdRef.current = turnstile.render(containerRef.current, {
          sitekey: TURNSTILE_SITE_KEY,
          language,
          callback: (token) => onVerifyRef.current(token),
          'expired-callback': () => onExpireRef.current(),
          'timeout-callback': () => onExpireRef.current(),
          'error-callback': () => onExpireRef.current(),
        });
      })
      .catch(() => onExpireRef.current());

    return () => {
      cancelled = true;

      if (widgetIdRef.current !== null && window.turnstile) {
        window.turnstile.remove(widgetIdRef.current);
        widgetIdRef.current = null;
      }
    };
  }, [language]);

  useEffect(() => {
    // Skip the initial render, there is nothing to reset yet.
    if (!resetSignal || widgetIdRef.current === null || !window.turnstile) {
      return;
    }

    window.turnstile.reset(widgetIdRef.current);
    onExpireRef.current();
  }, [resetSignal]);

  if (!TURNSTILE_SITE_KEY) {
    return null;
  }

  return <div ref={containerRef} className={className} />;
}

TurnstileWidget.propTypes = {
  onVerify: PropTypes.func.isRequired,
  onExpire: PropTypes.func.isRequired,
  resetSignal: PropTypes.number,
  className: PropTypes.string,
};

export default TurnstileWidget;
